import { Component, Provide, Vue } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import ConfigurationAutomationPersonnelAlgorithmsSelectionFormElement
    from '@/modules/configurationModule/detail/automation/personnel/algorithms-selection/components/form-element/configuration-automation-personnel-algorithms-selection-form-element.vue'
import {
    ConfigurationAutomationPersonnelAlgorithmsSelectionFormState,
    initialConfigurationAutomationPersonnelAlgorithmsSelectionFormState
} from '@/modules/configurationModule/detail/automation/personnel/algorithms-selection/components/form/configuration-automation-personnel-algorithms-selection-form.model'
import { configurationAutomaticAssignmentAlgorithmService } from '@/modules/configurationModule/detail/automation/personnel/algorithms-definition/services/configuration-automatic-assignment-algorithm/configuration-automatic-assignment-algorithm.service'
import { ConfigurationAutomaticAssignmentAlgorithmResponse } from '@/modules/configurationModule/detail/automation/personnel/algorithms-definition/services/configuration-automatic-assignment-algorithm/configuration-automatic-assignment-algorithm.dto'
import { configurationPersonnelAutomaticAssignmentConfigurationService } from '@/modules/configurationModule/detail/automation/personnel/algorithms-selection/services/configuration-personnel-automatic-assignment-configuration/configuration-personnel-automatic-assignment-configuration.service'
import {
    PersonnelAutomaticAssignmentConfigurationPatchRequest,
    PersonnelAutomaticAssignmentConfigurationResponse
} from '@/modules/configurationModule/detail/automation/personnel/algorithms-selection/services/configuration-personnel-automatic-assignment-configuration/configuration-personnel-automatic-assignment-configuration.dto'
import { CommonParams } from '@/api/resources/common/shared/models/common.models'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'

@Component({
    components: {
        ConfigurationSectionHeader,
        ConfigurationAutomationPersonnelAlgorithmsSelectionFormElement
    }
})
export default class ConfigurationAutomationPersonnelAlgorithmsSelectionForm extends Vue {
    state: ConfigurationAutomationPersonnelAlgorithmsSelectionFormState

    @Provide() externalValidator = this.$validator

    get modelValidated(): boolean {
        const hasErrors: boolean = this.$validator.errors.any()
        const isDirty: boolean = this.$_someFieldIsDirty(this.fields)
        return !hasErrors && isDirty
    }

    constructor() {
        super()
        this.state = initialConfigurationAutomationPersonnelAlgorithmsSelectionFormState()
    }

    private async _loadPersonnelAutomaticAssignment(): Promise<void> {
        this.state.personnelAutomaticAssignment = await this._getPersonnelAutomaticAssignment()
    }

    private async _loadAutomaticAssignmentAlgorithms(): Promise<void> {
        this.state.automaticAssignmentAlgorithms = await this._getAutomaticAssignmentAlgorithms()
    }

    async mounted() {
        await this._loadPersonnelAutomaticAssignment()
        await this._loadAutomaticAssignmentAlgorithms()
    }

    /// SERVICES CALLS ///

    private async _getAutomaticAssignmentAlgorithms(): Promise<ConfigurationAutomaticAssignmentAlgorithmResponse[]> {
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const response = await this.$_toCallUnique(configurationAutomaticAssignmentAlgorithmService.paginator(undefined, params, this.state.laboratoryId))
        if (response) {
            return response.content
        }
        return []
    }

    private async _getPersonnelAutomaticAssignment(): Promise<PersonnelAutomaticAssignmentConfigurationResponse | null> {
        const response = await this.$_toCallUnique(configurationPersonnelAutomaticAssignmentConfigurationService.get(this.state.laboratoryId))
        if (response) {
            return response
        }
        return null
    }

    private async _patchPersonnelAutomaticAssignment(patchRequest: PersonnelAutomaticAssignmentConfigurationPatchRequest): Promise<void> {
        if (patchRequest) {
            await this.$_toCallUnique(configurationPersonnelAutomaticAssignmentConfigurationService.patch(this.state.laboratoryId, patchRequest))
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated'))
        }
    }

    /// FORM OPERATIONS ///

    private async _patchOperation(): Promise<void> {
        if (this.state.personnelAutomaticAssignment) {
            const patchRequest: PersonnelAutomaticAssignmentConfigurationPatchRequest = {
                casePathologistAlgorithmId: this.state.personnelAutomaticAssignment.casePathologistAlgorithm?.id || null,
                caseResidentAlgorithmId: this.state.personnelAutomaticAssignment.caseResidentAlgorithm?.id || null,
                sampleCytologyExpertAlgorithmId: this.state.personnelAutomaticAssignment.sampleCytologyExpertAlgorithm?.id || null,
                sampleGrossingResponsibleAlgorithmId: this.state.personnelAutomaticAssignment.sampleGrossingResponsibleAlgorithm?.id || null,
                sampleProcessingResponsibleAlgorithmId: this.state.personnelAutomaticAssignment.sampleProcessingResponsibleAlgorithm?.id || null
            }
            await this._patchPersonnelAutomaticAssignment(patchRequest)
            await this._loadPersonnelAutomaticAssignment()
        }
    }

    async doAction(): Promise<void> {
        const valid = await this.$validator.validateAll()
        if (valid) {
            await this._patchOperation()
            await this.$validator.reset()
        }
    }
}
